import axios from 'axios';

// Prayer community client. The feature is now fully in-app: it talks to the HOGC
// One API (hogc-one-api `/api/prayer/*`) using the member's own Auth0 token, so
// there is no session handoff, no external URL, and identity is req.memberId on
// the server — nothing is client-asserted. See hogc-one-api src/features/prayer.

const endpoint = path => `${process.env.VUE_APP_API_URL}/prayer${path}`;

const authHeaders = rootState => ({
  headers: { Authorization: `Bearer ${rootState.user?.token}` },
});

const errorCodeOf = error =>
  error?.response?.data?.code || (error?.response ? 'request_failed' : 'network_error');

const INITIAL_STATE = {
  state: null, // the full GET /api/prayer/state body
  // GET /api/prayer/config. Static for the life of a release. Only `badges` is read today — it
  // is what lets the shelf draw what a member has NOT earned yet, and the shelf is how the
  // feature explains itself, so without this a new member sees an empty screen. The payload
  // also carries the level ladder; nothing consumes it, because the avatar derives its own.
  config: null,
  configLoading: false,
  phase: 'loading', // loading | needsCg | optIn | home | error
  errorCode: null,
  busy: false, // a clock-in / opt-in / save is in flight
  celebration: null, // { leveledUp, newBadges } from the last clock-in
};

export default {
  namespaced: true,
  state: () => ({ ...INITIAL_STATE }),
  getters: {
    member: state => state.state?.member ?? null,
    cg: state => state.state?.cg ?? null,
    zone: state => state.state?.zone ?? null,
    circle: state => state.state?.cg?.circle ?? [],
    prayedToday: state => Boolean(state.state?.member?.prayedToday),
    optedIn: state => Boolean(state.state?.member?.optedIn),
    // Empty until the config call lands, which is what the shelf checks before rendering —
    // a half-drawn shelf is worse than none.
    badgeCatalogue: state => state.config?.badges ?? [],
  },
  mutations: {
    setConfig(state, payload) {
      state.config = payload;
    },
    setConfigLoading(state, loading) {
      state.configLoading = loading;
    },
    setState(state, payload) {
      state.state = payload;
      // No CG on the member's profile → nothing to pray with as a circle yet.
      if (!payload?.cg) state.phase = 'needsCg';
      else state.phase = payload.member?.optedIn ? 'home' : 'optIn';
    },
    setPhase(state, phase) {
      state.phase = phase;
    },
    setError(state, code) {
      state.errorCode = code;
      state.phase = 'error';
    },
    setBusy(state, busy) {
      state.busy = busy;
    },
    setCelebration(state, celebration) {
      state.celebration = celebration;
    },
    reset(state) {
      Object.assign(state, INITIAL_STATE);
    },
  },
  actions: {
    /**
     * The badge catalogue. Fetched once and kept — it cannot change without a release — and
     * deliberately silent on failure: the shelf simply does not render, which is a smaller loss
     * than sending the whole page to its error state over decoration.
     */
    async fetchConfig({ commit, state, rootState }) {
      // `config` alone only guards after the response lands, and ionViewWillEnter re-runs load()
      // on every entry to the page — so leaving and coming straight back would fire a second
      // identical request while the first was still open.
      if (state.config || state.configLoading) return;
      commit('setConfigLoading', true);
      try {
        const { data } = await axios.get(endpoint('/config'), authHeaders(rootState));
        commit('setConfig', data);
      } catch {
        // Left null; the badge shelf checks for it.
      } finally {
        commit('setConfigLoading', false);
      }
    },

    async fetchState({ commit, rootState }) {
      commit('setPhase', 'loading');
      try {
        const { data } = await axios.get(endpoint('/state'), authHeaders(rootState));
        commit('setState', data);
      } catch (error) {
        commit('setError', errorCodeOf(error));
      }
    },

    async optIn({ commit, rootState }) {
      commit('setBusy', true);
      try {
        const { data } = await axios.post(endpoint('/opt-in'), null, authHeaders(rootState));
        commit('setState', data);
      } catch (error) {
        commit('setError', errorCodeOf(error));
      } finally {
        commit('setBusy', false);
      }
    },

    async clockIn({ commit, rootState }) {
      commit('setBusy', true);
      try {
        const { data } = await axios.post(endpoint('/clock-in'), null, authHeaders(rootState));
        commit('setState', data);
        if (!data.alreadyClockedIn) {
          commit('setCelebration', {
            leveledUp: Boolean(data.leveledUp),
            newBadges: data.newBadges || [],
          });
        }
      } catch (error) {
        commit('setError', errorCodeOf(error));
      } finally {
        commit('setBusy', false);
      }
    },

    async saveAvatar({ commit, rootState }, avatar) {
      commit('setBusy', true);
      try {
        const { data } = await axios.post(endpoint('/avatar'), avatar, authHeaders(rootState));
        commit('setState', data);
      } catch (error) {
        commit('setError', errorCodeOf(error));
      } finally {
        commit('setBusy', false);
      }
    },

    async saveName({ commit, rootState }, name) {
      commit('setBusy', true);
      try {
        const { data } = await axios.post(endpoint('/name'), { name }, authHeaders(rootState));
        commit('setState', data);
      } catch (error) {
        commit('setError', errorCodeOf(error));
      } finally {
        commit('setBusy', false);
      }
    },
  },
};
